import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactComponent as GridIcon } from '@/assets/homepage/grid.svg';
import { ReactComponent as ShareIcon } from '@/assets/homepage/share-2.svg';
import { ReactComponent as PentoolIcon } from '@/assets/homepage/pen-tool.svg';
import { ReactComponent as GridIconAt } from '@/assets/homepage/grid-at.svg';
import { ReactComponent as ShareIconAt } from '@/assets/homepage/share-2-at.svg';
import { ReactComponent as PentoolIconAt } from '@/assets/homepage/pen-tool-at.svg';
import PrimaryButton from '@/components/shacdn/PrimaryButton';

const services = [
  {
    title: 'UI/UX Design',
    desc: 'We craft intuitive and beautiful digital experiences that your users will love. From wireframes to final interfaces, our design process is user-centered and business-driven.',
    icon: GridIcon,
    active: GridIconAt
  },
  {
    title: 'IT Outsourcing',
    desc: 'We provide reliable IT outsourcing services to help you scale your business efficiently and cost-effectively.',
    icon: ShareIcon,
    active: ShareIconAt
  },
  {
    title: 'Digital Marketing',
    desc: 'Grow your brand with our smart digital marketing strategies tailored to your business goals.',
    icon: PentoolIcon,
    active: PentoolIconAt
  }
];

const AUTO_ADVANCE_INTERVAL = 5000;

const cardVariants = {
  inactive: {
    scale: 1
  },
  active: {
    scale: 1.02
  }
};

const descVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 200, damping: 24 }
  },
  exit: {
    opacity: 0,
    y: 10,
    transition: { type: 'spring' as const, stiffness: 200, damping: 24 }
  }
};

const ServicesSection: React.FC = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!paused) {
      timerRef.current = setInterval(() => {
        setActive((prev) => (prev + 1) % services.length);
      }, AUTO_ADVANCE_INTERVAL);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [paused]);

  const handleHover = (index: number) => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setActive(index);
      setPaused(true);
    }, 100); // debounce hover
  };

  const handlePause = () => setPaused(true);
  const handleResume = () => setPaused(false);

  return (
    <section className='relative w-full py-20 bg-white z-10'>
      {/* Preload icons to avoid flicker */}
      <div className='hidden'>
        {services.map((s, i) => (
          <React.Fragment key={i}>
            <s.icon />
            <s.active />
          </React.Fragment>
        ))}
      </div>

      <div className='max-w-6xl mx-auto px-4'>
        <motion.h2
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className='text-4xl font-semibold mb-12 text-center max-[560px]:text-3xl'
        >
          Our services
        </motion.h2>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              tabIndex={0}
              role='button'
              aria-pressed={active === i}
              onClick={() => {
                setActive(i);
                setPaused(true);
              }}
              onMouseEnter={() => handleHover(i)}
              onMouseLeave={handleResume}
              onFocus={handlePause}
              onBlur={handleResume}
              className={`flex justify-between w-full p-8 max-[560px]:p-4 cursor-pointer outline-none transition-all duration-200 ${
                active === i
                  ? 'bg-[linear-gradient(90deg,rgba(37,99,235,0)_0%,rgba(37,99,235,0.2)_50%,rgba(37,99,235,0)_100%)] border-y-2 border-solid [border-image-source:linear-gradient(270deg,rgba(255,255,255,0)_0%,#2563EB_50%,rgba(255,255,255,0)_100%)] [border-image-slice:1]'
                  : 'opacity-80 hover:opacity-100 border-y border-solid [border-image-source:linear-gradient(270deg,rgba(238,238,238,0)_0%,#DDDDDD_50%,rgba(238,238,238,0)_100%)] [border-image-slice:1]'
              }`}
              variants={cardVariants}
              animate={active === i ? 'active' : 'inactive'}
              initial={false}
            >
              <div className='flex flex-col items-start w-[calc(100%-100px)]'>
                <div
                  className={`text-[56px] min-h-[80px] max-[768px]:text-[28px] max-[560px]:text-[20px] text-[#98989A] font-bold mb-2 ${
                    active === i && '!text-[#2563EB]'
                  }`}
                >
                  {service.title}
                </div>
                <AnimatePresence mode='wait'>
                  {active === i && (
                    <motion.div
                      key={`desc-${i}`}
                      variants={descVariants}
                      initial='hidden'
                      animate='visible'
                      exit='exit'
                      className='text-base max-[560px]:text-sm text-gray-600'
                    >
                      {service.desc}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div
                className={`w-20 h-20 max-[560px]:w-10 max-[560px]:h-10 flex items-center justify-center rounded-2xl ${
                  active === i ? 'bg-[#D1E0FF] border-[1px] border-[#BDD6FF]' : 'bg-[#F2F2F2]'
                }`}
              >
                {active === i ? <service.active className='w-6' /> : <service.icon className='w-6' />}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className='w-full flex items-center justify-center mt-16'
        >
          <PrimaryButton>Our projects</PrimaryButton>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
